import { type AnyAction } from 'redux';
import { call, put, takeLatest, type CallEffect, type ForkEffect, type PutEffect } from 'redux-saga/effects';
import { DashboardActionType } from './enums';
import {
	fetchProjectCosts,
	fetchProjectOverview,
	fetchStatusSummary,
	generatePromoCode,
	fetchDashboardProjects,
	fetchDashboardTickets,
	fetchDashboardApprovals,
	fetchPromoCodeHistory,
	createCustomPromoCode,
	fetchAnalyticsData,
	fetchFinancialData,
	fetchCRMData,
	fetchExecutiveData
} from './api';
import { handleEmptyNullValues } from '@utilities';

type DashboardSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type DashboardSagaForkEffect = Generator<ForkEffect<void>>;

export function* fetchProjectCostsEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchProjectCosts);
		yield put({
			type: DashboardActionType.STORE_PROJECT_COSTS,
			payload: data
		});
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchProjectOverviewEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchProjectOverview);
		yield put({
			type: DashboardActionType.STORE_PROJECT_OVERVIEW,
			payload: data
		});
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchStatusSummaryEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchStatusSummary);
		yield put({
			type: DashboardActionType.STORE_STATUS_SUMMARY,
			payload: data
		});
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchDashboardProjectsEffect(action: AnyAction): DashboardSagaEffect {
	const { lazyState, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchDashboardProjects, handleEmptyNullValues(lazyState));
		yield put({
			type: DashboardActionType.STORE_DASHBOARD_PROJECTS,
			payload: data
		});
	} catch (error) {
		// errorCB && errorCB(error);
		console.log(error);
	}
}

export function* fetchDashboardApprovalsEffect(action: AnyAction): DashboardSagaEffect {
	const { lazyState, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchDashboardApprovals, lazyState);
		yield put({
			type: DashboardActionType.STORE_DASHBOARD_APPROVALS,
			payload: data
		});
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchDashboardTicketsEffect(action: AnyAction): DashboardSagaEffect {
	const { lazyState, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchDashboardTickets, lazyState);
		yield put({
			type: DashboardActionType.STORE_DASHBOARD_TICKETS,
			payload: data
		});
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchPromoCodeEffect(action: AnyAction): DashboardSagaEffect {
	const { leadId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(generatePromoCode, leadId);
		successCB(response.data);
	} catch (error: unknown) {
		errorCB(error);
	}
}

export function* fetchPromoCodeHistoryEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload || {};
	try {
		const { data }: any = yield call(fetchPromoCodeHistory);
		yield put({
			type: DashboardActionType.STORE_PROMO_CODE_HISTORY,
			payload: data
		});
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* createCustomPromoCodeEffect(action: AnyAction): DashboardSagaEffect {
	const { payload, successCB, errorCB } = action;
	try {
		const response = yield call(createCustomPromoCode, payload);
		successCB && successCB(response.data);
	} catch (error: unknown) {
		errorCB && errorCB(error);
	}
}

export function* fetchPromoCodeSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_PROMO_CODE, fetchPromoCodeEffect);
}

export function* fetchProjectsCostsSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_PROJECT_COSTS, fetchProjectCostsEffect);
}

export function* fetchProjectOverviewSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_PROJECT_OVERVIEW, fetchProjectOverviewEffect);
}

export function* fetchStatusSummarySaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_STATUS_SUMMARY, fetchStatusSummaryEffect);
}

export function* fetchDashboardProjectsSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_DASHBOARD_PROJECTS, fetchDashboardProjectsEffect);
}

export function* fetchDashboardApprovalsSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_DASHBOARD_APPROVALS, fetchDashboardApprovalsEffect);
}

export function* fetchDashboardTicketsSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_DASHBOARD_TICKETS, fetchDashboardTicketsEffect);
}

export function* fetchPromoCodeHistorySaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_PROMO_CODE_HISTORY, fetchPromoCodeHistoryEffect);
}

export function* createCustomPromoCodeSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.CREATE_CUSTOM_PROMO_CODE, createCustomPromoCodeEffect);
}

export function* fetchAnalyticsDataEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchAnalyticsData);
		yield put({ type: DashboardActionType.STORE_ANALYTICS_DATA, payload: data });
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchFinancialDataEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchFinancialData);
		yield put({ type: DashboardActionType.STORE_FINANCIAL_DATA, payload: data });
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchCRMDataEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchCRMData);
		yield put({ type: DashboardActionType.STORE_CRM_DATA, payload: data });
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchExecutiveDataEffect(action: AnyAction): DashboardSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchExecutiveData);
		yield put({ type: DashboardActionType.STORE_EXECUTIVE_DATA, payload: data });
	} catch (error) {
		errorCB && errorCB(error);
	}
}

export function* fetchAnalyticsDataSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_ANALYTICS_DATA, fetchAnalyticsDataEffect);
}

export function* fetchFinancialDataSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_FINANCIAL_DATA, fetchFinancialDataEffect);
}

export function* fetchCRMDataSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_CRM_DATA, fetchCRMDataEffect);
}

export function* fetchExecutiveDataSaga(): DashboardSagaForkEffect {
	yield takeLatest(DashboardActionType.FETCH_EXECUTIVE_DATA, fetchExecutiveDataEffect);
}
